import { useState } from 'react'
import { motion } from 'motion/react'
import { ArrowRight, ArrowLeft, Check, Lock } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/cn'
import type { IntakeFormData, ServiceId } from '@/hooks/useIntakeWizard'

interface ServicesStepProps {
  formData: IntakeFormData
  updateField: <K extends keyof IntakeFormData>(field: K, value: IntakeFormData[K]) => void
  onNext: () => void
  onBack: () => void
}

interface ServiceOption {
  value: ServiceId
  label: string
  description: string
  locked?: boolean
}

const BUILD_LAUNCH_SERVICES: ServiceOption[] = [
  {
    value: 'cms-platform',
    label: 'CMS platform',
    description: '$20/mo base — you manage your own content. This is the floor for every plan.',
    locked: true,
  },
  {
    value: 'new-website-content-ready',
    label: 'New website build (I have content ready)',
    description: 'You bring the copy and images, we build the site',
  },
  {
    value: 'new-website-with-coaching',
    label: 'New website build with content coaching',
    description: 'We help you shape copy, structure, and visuals',
  },
  {
    value: 'branding-logo',
    label: 'Branding / logo design',
    description: 'Logo, color system, typography from Danielle',
  },
  {
    value: 'custom-design',
    label: 'Custom design / brand polish',
    description: 'Beyond templates: custom visual direction',
  },
]

const ONGOING_SERVICES: ServiceOption[] = [
  {
    value: 'ongoing-updates',
    label: 'Updates managed by Garrett',
    description: 'Email or request form; I handle changes for you',
  },
  {
    value: 'email-marketing',
    label: 'Email marketing',
    description: 'Newsletters, campaigns, automation',
  },
  {
    value: 'social-media',
    label: 'Social media content',
    description: 'Regular posts across your channels',
  },
]

function ServiceCheckbox({
  option,
  selected,
  onToggle,
}: {
  option: ServiceOption
  selected: boolean
  onToggle: () => void
}) {
  const disabled = option.locked
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      disabled={disabled}
      className={cn(
        'w-full rounded-lg border-2 px-5 py-4 text-left transition-all',
        disabled ? 'cursor-not-allowed opacity-90' : 'hover:border-primary/50 hover:bg-primary/5',
        selected ? 'border-primary bg-primary/5' : 'border-border'
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2',
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
          )}
        >
          {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-base font-medium">{option.label}</span>
            {disabled && <Lock className="text-muted-foreground h-3 w-3" />}
          </div>
          <p className="text-muted-foreground mt-0.5 text-sm">{option.description}</p>
        </div>
      </div>
    </button>
  )
}

export function ServicesStep({ formData, updateField, onNext, onBack }: ServicesStepProps) {
  const [error, setError] = useState<string | null>(null)

  const toggleService = (value: ServiceId) => {
    setError(null)
    if (value === 'cms-platform') return // locked
    const current = formData.services
    if (current.includes(value)) {
      updateField(
        'services',
        current.filter((s) => s !== value)
      )
    } else {
      updateField('services', [...current, value])
    }
  }

  const handleNext = () => {
    // Always valid — cms-platform is pre-checked, so services.length is always >= 1
    if (formData.services.length === 0) {
      setError('Please select at least one service')
      return
    }
    onNext()
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground text-3xl font-bold md:text-4xl">What can we help with?</h2>
        <p className="text-muted-foreground text-lg">
          Pick anything that sounds useful. We'll sort out the details live.
        </p>
      </motion.div>

      {/* Build & launch group */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-3"
      >
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Build &amp; launch</h3>
        <div className="grid gap-2">
          {BUILD_LAUNCH_SERVICES.map((option) => (
            <ServiceCheckbox
              key={option.value}
              option={option}
              selected={formData.services.includes(option.value)}
              onToggle={() => toggleService(option.value)}
            />
          ))}
        </div>
      </motion.div>

      {/* Ongoing monthly group */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-3"
      >
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Ongoing monthly</h3>
        <div className="grid gap-2">
          {ONGOING_SERVICES.map((option) => (
            <ServiceCheckbox
              key={option.value}
              option={option}
              selected={formData.services.includes(option.value)}
              onToggle={() => toggleService(option.value)}
            />
          ))}
        </div>
      </motion.div>

      {error && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-red-500">
          {error}
        </motion.p>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button type="button" size="lg" onClick={handleNext} className="flex-1 md:min-w-[200px] md:flex-none">
          Continue
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="text-muted-foreground text-sm"
      >
        Step 3 of 5
      </motion.p>
    </div>
  )
}
